/**
 * Lock-screen metadata + background-resilient ambient mirror.
 *
 * Two responsibilities:
 *  1. setSessionMetadata({ characterName, atmosphereName, kind }) — keeps the
 *     lock-screen / media-session card showing a CONTEXTUAL title
 *     ("Rainy Cabin with Serine", "A Quiet Night with Elizabeth") instead of
 *     the default ("Continua"). The title is also
 *     mirrored into document.title for the browser tab.
 *  2. attachAmbientStreamToAudio(mixer, audioEl) — mirrors the AmbientMixer's
 *     synthesized output into an <audio> element via MediaStreamDestination
 *     so iOS Safari / Android Chrome keep the ambient playing under the lock
 *     screen (where they would normally suspend the AudioContext).
 */

const TITLE_TEMPLATES = {
  evening_named: (atmo, name) => `${atmo} with ${name}`,
  evening_unnamed: (atmo) => `Evening at the ${atmo.toLowerCase()}`,
  morning_named: (name) => `A Quiet Morning with ${name}`,
  morning_unnamed: () => "A Quiet Morning",
  comforting: (name) => `${name} is keeping you company`,
};

function _pickTitle({ characterName, atmosphereName, kind }) {
  const atmo = (atmosphereName || "").trim();
  const cname = (characterName || "").trim();
  if (kind === "morning") {
    return cname ? TITLE_TEMPLATES.morning_named(cname) : TITLE_TEMPLATES.morning_unnamed();
  }
  if (cname && atmo) return TITLE_TEMPLATES.evening_named(atmo, cname);
  if (cname) return TITLE_TEMPLATES.comforting(cname);
  if (atmo) return TITLE_TEMPLATES.evening_unnamed(atmo);
  return "Continua";
}

/**
 * Set lock-screen / media-session metadata. The companion name MUST match the
 * active session — never show another character's name. Safe to call from any
 * mount; clears cleanly via clearSessionMetadata().
 */
export function setSessionMetadata({ characterName, atmosphereName, kind, artworkUrl } = {}) {
  const title = _pickTitle({ characterName, atmosphereName, kind });
  // Browser tab title
  try { document.title = `${title} · Continua`; } catch (e) { /* ignore */ }
  // MediaSession (iOS Safari, Android Chrome, desktop Chrome/Edge lock screens)
  if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
    try {
      // MIME follows the actual asset (character art may be jpeg or png).
      const type = /\.jpe?g($|\?)/i.test(artworkUrl || "") ? "image/jpeg" : "image/png";
      const artwork = artworkUrl
        ? [
            { src: artworkUrl, sizes: "512x512", type },
            { src: artworkUrl, sizes: "384x384", type },
            { src: artworkUrl, sizes: "256x256", type },
            { src: artworkUrl, sizes: "192x192", type },
            { src: artworkUrl, sizes: "128x128", type },
            { src: artworkUrl, sizes: "96x96", type },
          ]
        : [];
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title,
        artist: characterName || "Continua",
        album: atmosphereName || "Continua",
        artwork,
      });
    } catch (e) { /* MediaMetadata unsupported */ }
  }
}

export function clearSessionMetadata() {
  try { document.title = "Continua"; } catch (e) { /* ignore */ }
  if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
    try { navigator.mediaSession.metadata = null; } catch (e) { /* ignore */ }
  }
}

/**
 * Wire play/pause handlers on the MediaSession so lock-screen / headphone /
 * AirPods buttons control the dialogue audio element (and the coupled mixer
 * via the existing onPlay/onPause callbacks).
 */
export function bindMediaSessionControls({ onPlay, onPause }) {
  if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
  try {
    navigator.mediaSession.setActionHandler("play", () => { try { onPlay && onPlay(); } catch (e) { /* ignore */ } });
    navigator.mediaSession.setActionHandler("pause", () => { try { onPause && onPause(); } catch (e) { /* ignore */ } });
  } catch (e) { /* unsupported handler — older browsers */ }
}

/**
 * Mirror the AmbientMixer's synthesized output into a hidden <audio> element
 * so iOS / Android keep the ambient running under the lock screen.
 *
 * Returns true on success. Falls back gracefully if MediaStreamDestination is
 * unsupported — ambient still plays through the AudioContext directly, just
 * without lock-screen resilience.
 */
export function attachAmbientStreamToAudio(mixer, audioEl) {
  if (!mixer || !audioEl) return false;
  // The mixer owns the muting/visibility logic so the hidden element is only
  // audible while backgrounded — this avoids both the whine (foreground) and
  // the doubling (both paths audible at once).
  if (typeof mixer.attachBackgroundAudio === "function") {
    return mixer.attachBackgroundAudio(audioEl);
  }
  try {
    const stream = mixer.getMediaStream && mixer.getMediaStream();
    if (!stream) return false;
    audioEl.srcObject = stream;
    audioEl.muted = false;
    audioEl.volume = 1;
    const p = audioEl.play();
    if (p && p.catch) p.catch(() => { /* user-gesture required — retry on first interaction */ });
    return true;
  } catch (e) {
    return false;
  }
}
